/**
 * The skyHire role catalog — the single source of truth for system roles.
 *
 * Two things live here that used to be scattered:
 *
 *  1. **The role definitions** (permissions + navigation), previously inline in
 *     `server/db/seedSystemRolesOnBoot.ts` and duplicated in
 *     `scripts/migrations/rbac-seed.ts`. Roles that exist only in the database
 *     and nowhere in code (`vendor` was one) are how the catalog silently
 *     drifts, so every role skyHire recognises is declared below.
 *  2. **`scope`** — the one scope a role may be assigned at. skyHire grants at
 *     three scopes (global > tenant > instance) but never recorded which scope a
 *     given role is *for*, so `super_admin` ended up assigned at all three and
 *     stopped meaning anything in particular.
 *
 * ## The ladder
 *
 * | role             | scope    | reach                                  |
 * | ---------------- | -------- | -------------------------------------- |
 * | `skypoint_admin` | global   | every tenant — Skypoint internal staff |
 * | everything else  | instance | one instance                           |
 *
 * Two tiers, the model skySchedule runs and the one skySupport's UAM contract is
 * written for: skySupport grants and revokes one instance at a time, so a role
 * that reaches further than an instance is one it can neither see nor manage.
 * Someone who administers several instances holds Admin on each of them.
 *
 * `super_admin` — "every instance in one tenant" — is RETIRED (see `retired`).
 * Its rows are converted to per-instance `admin` by
 * `scripts/migrations/fix-role-scope.ts`; until that runs they read as Admin
 * everywhere through {@link currentRoleName}.
 *
 * ## `name` is a frozen key, `label` is display
 *
 * `name` is the join key: UAM publishes it as `role.key` (skySupport stores and
 * joins on it), `ROLE_HIERARCHY` is keyed on it, and ~79 call sites compare it
 * literally. Renaming one silently breaks all of them. `label` carries the
 * display name and is what tenant configuration is free to change.
 */

import type { DataScope } from "./types/accessScope";
import { DEFAULT_PRODUCT, type ProductKey } from "./products/keys";
// A value import from the shared performance contract (not product code, so no boundary rule
// covers it). That module imports only a type from here, so there is no runtime cycle.
import { PERFORMANCE_ROLES } from "./products/performance/roles";
// The same shape for skyPerformance IT's shared contract (spec 2026-10-26 §4.4): a type-only import
// back, so no runtime cycle.
import { PERFORMANCE_IT_ROLES } from "./products/performance-it/roles";

/** Where a role may be assigned. Mirrors `user_role_assignments`' three scopes. */
export type RoleScope = "global" | "tenant" | "instance";

export type RoleCategory = "Admin" | "Manager" | "Staff" | "Viewer";

export interface SystemRoleDefinition {
  /** Frozen join key — `roles.name`, UAM's `role.key`. Never rename. */
  name: string;
  /** Display name — `roles.label`, UAM's `role.name`. Safe to change. */
  label: string;
  description: string;
  /**
   * What `description` said before it was reworded. The boot seed keeps a stored description (an
   * admin may have written their own), except one still reading a former catalog text: that row was
   * never edited, so it moves to the current one.
   */
  formerDescriptions?: string[];
  category: RoleCategory;
  /** The only scope this role may be assigned at. */
  scope: RoleScope;
  /**
   * Which product this role belongs to (`public.roles.product_key`). Absent means hire, so the
   * eight existing roles need no edit. A role never carries another product's keys (spec §2).
   */
  product?: ProductKey;
  /**
   * What slice of hiring data the role reaches (`public.roles.data_scope`, migrations/0012).
   * Code-owned: the boot seed writes it to the shared row and every instance copy.
   * Communities and departments can restrict only `communities` and `assigned_jobs`.
   */
  dataScope: DataScope;
  isSystem: boolean;
  /** Kept out of role pickers and out of UAM's `/roles` catalog. */
  isHidden: boolean;
  /** Permissions and navigation cannot be edited. */
  isImmutable: boolean;
  /**
   * No longer grantable anywhere, and never shown. The catalog keeps the entry so
   * rows that still hold it resolve, and so the boot seed keeps the database row
   * hidden instead of dropping it. {@link RETIRED_ROLE_REPLACEMENT} names what
   * such a row is treated as until the cleanup script converts it.
   */
  retired?: boolean;
  permissions: Record<string, boolean>;
  navigation: Record<string, boolean>;
}

const ALL_PERMISSIONS = {
  "applicants.view": true,
  "applicants.create": true,
  "applicants.update": true,
  "applicants.delete": true,
  "jobs.view": true,
  "jobs.create": true,
  // Editing a job had no key at all — the matrix offered view/create/publish/
  // delete, so "may change a posting" was unexpressible and every edit route ran
  // ungated. Paired with jobs.create behind one "Create & edit" switch: nobody
  // wants a role that can post a job but not fix a typo in it.
  "jobs.update": true,
  "jobs.publish": true,
  "jobs.delete": true,
  // Offers carry compensation. They were governed by nothing, which meant a
  // coordinator who could open a candidate could read their comp. Split view
  // from manage because approving an offer is a different act from seeing one.
  "offers.view": true,
  "offers.manage": true,
  // The Inbox shows cross-applicant email and SMS threads, salary and offer
  // context included. Today it is hardcoded admin-only in the sidebar; this
  // makes that a decision a customer can make instead of a constant.
  "inbox.view": true,
  "interviews.view": true,
  "interviews.schedule": true,
  "interviews.feedback": true,
  // Reports had NO key at all until now — the whole router sat behind
  // `requireAdmin`, which is why Role Management showed no Reports row and why
  // neither Team Member nor Limited Team Member could open the page. The
  // Limited Team Member description has promised reports for the job postings
  // they hire for the whole time; this is the key that lets that become true.
  //
  // Holding the key means "may open Reports". It says nothing about WHICH
  // postings are counted — that is a scope question, answered per role by the
  // caller's accessible job set, and it must never be widened by the saved
  // layout (see reportJobScope.ts, which is a display filter the user controls).
  "reports.view": true,
  "settings.view": true,
  "settings.manage": true,
  "users.view": true,
  "users.manage": true,
  "apikeys.manage": true,
  "integrations.manage": true,
  // Candidate documents in the customer's SharePoint. Admins only as shipped:
  // these are identity documents, and seeing a candidate is not a reason to see
  // their passport. An admin may switch either on for another role.
  "documents.view": true,
  "documents.manage": true,
  // Referring a candidate is open to every role. The Interviewer role in
  // particular has always SAID it can refer — "Interviewers can be scheduled for
  // interviews, complete feedback, and refer candidates" — while holding none of
  // the keys that allow it, so the role did not do what its own description
  // promised.
  "jobs.viewPublished": true,
  "referrals.create": true,
  "referrals.viewOwn": true,
  "rewards.viewOwn": true,
};

/**
 * Identical to `ALL_PERMISSIONS`, on purpose.
 *
 * `admin` and `super_admin` differ by SCOPE, not by power: an admin has full
 * access to one instance, a super admin has the same access across every
 * instance in the tenant, and the platform tier has it across every tenant.
 * That is the ladder this file documents at the top, and it is enforced by
 * `scope` on each role definition.
 *
 * This map used to withhold `jobs.delete`, `applicants.delete`,
 * `apikeys.manage` and `integrations.manage` from `admin`, which quietly turned
 * a scope distinction into a capability one — an "instance administrator" who
 * could not delete a job in the instance they administer.
 *
 * It never showed, because none of those keys were enforced: the delete routes
 * ran ungated, so every role could delete regardless of what this said. Making
 * the switches real is what would have turned a dormant mis-specification into
 * an admin who suddenly cannot do their job.
 */
const ADMIN_PERMISSIONS = { ...ALL_PERMISSIONS };

const ALL_NAVIGATION = {
  showDashboard: true,
  showLeads: true,
  // The Inbox and Reports entries were pushed into the sidebar by a hardcoded
  // `isAdmin` check with no nav key behind them, which is why Role Management
  // listed six sidebar switches for a sidebar with eight sections — and why
  // #807 reports "Reports" missing from Sidebar Visibility. Every section the
  // sidebar can render now has a key here.
  showInbox: true,
  showJobs: true,
  showTours: true,
  showReports: true,
  showSettings: true,
  showUsers: true,
};

const TEAM_MEMBER_PERMISSIONS = {
  "applicants.view": true,
  "applicants.create": true,
  "applicants.update": true,
  "applicants.delete": false,
  "jobs.view": true,
  "jobs.create": false,
  "jobs.update": false,
  "jobs.publish": false,
  "jobs.delete": false,
  // New keys default OFF for every role below admin. They are additive, so a
  // role gains nothing until someone turns them on — which keeps introducing
  // them from quietly widening access the day they start being enforced.
  "offers.view": false,
  "offers.manage": false,
  "inbox.view": false,
  "interviews.view": true,
  "interviews.schedule": true,
  "interviews.feedback": true,
  // The whole desk. This role is the TA Manager persona: it already sees every
  // candidate and every posting with no context filter, so scoping its reports
  // to a subset would REMOVE reporting it effectively has today.
  "reports.view": true,
  "settings.view": false,
  "settings.manage": false,
  "users.view": false,
  "users.manage": false,
  "apikeys.manage": false,
  "integrations.manage": false,
  "documents.view": false,
  "documents.manage": false,
  // Referring a candidate is open to every role. The Interviewer role in
  // particular has always SAID it can refer — "Interviewers can be scheduled for
  // interviews, complete feedback, and refer candidates" — while holding none of
  // the keys that allow it, so the role did not do what its own description
  // promised.
  "jobs.viewPublished": true,
  "referrals.create": true,
  "referrals.viewOwn": true,
  "rewards.viewOwn": true,
};

const TEAM_MEMBER_NAVIGATION = {
  showDashboard: true,
  showLeads: true,
  showInbox: false,
  showJobs: true,
  showTours: true,
  // The role holds `reports.view`; the sidebar had no key to render it with, so
  // the page it is allowed to open was unreachable (#807).
  showReports: true,
  showSettings: true,
  showUsers: false,
};

const LIMITED_TEAM_MEMBER_PERMISSIONS = {
  "applicants.view": true,
  "applicants.create": false,
  "applicants.update": false,
  "applicants.delete": false,
  "jobs.view": true,
  "jobs.create": false,
  "jobs.update": false,
  "jobs.publish": false,
  "jobs.delete": false,
  "offers.view": false,
  "offers.manage": false,
  "inbox.view": false,
  "interviews.view": true,
  "interviews.schedule": false,
  "interviews.feedback": true,
  // The Recruiter persona, and the reason this key exists. Holding it opens the
  // page; it does NOT mean instance-wide numbers. This role is already job
  // scoped (jobTeamAccessService.isJobScopedRole), so its reports must count
  // only its own postings — otherwise a recruiter who cannot open a job could
  // still read its pipeline off a report tile.
  "reports.view": true,
  "settings.view": false,
  "settings.manage": false,
  "users.view": false,
  "users.manage": false,
  "apikeys.manage": false,
  "integrations.manage": false,
  "documents.view": false,
  "documents.manage": false,
  // Referring a candidate is open to every role. The Interviewer role in
  // particular has always SAID it can refer — "Interviewers can be scheduled for
  // interviews, complete feedback, and refer candidates" — while holding none of
  // the keys that allow it, so the role did not do what its own description
  // promised.
  "jobs.viewPublished": true,
  "referrals.create": true,
  "referrals.viewOwn": true,
  "rewards.viewOwn": true,
};

const LIMITED_TEAM_MEMBER_NAVIGATION = {
  showDashboard: true,
  showLeads: true,
  showInbox: false,
  showJobs: true,
  showTours: false,
  showReports: true,
  showSettings: true,
  showUsers: false,
};

const INTERVIEWER_PERMISSIONS = {
  "applicants.view": true,
  "applicants.create": false,
  "applicants.update": false,
  "applicants.delete": false,
  "jobs.view": false,
  "jobs.create": false,
  "jobs.publish": false,
  "jobs.delete": false,
  "interviews.view": true,
  "interviews.schedule": false,
  "interviews.feedback": true,
  "settings.view": false,
  "settings.manage": false,
  "users.view": false,
  "users.manage": false,
  "apikeys.manage": false,
  "integrations.manage": false,
  "documents.view": false,
  "documents.manage": false,
  // An interviewer is scoped to the applications they are scheduled on, which is
  // narrower than any posting. There is no report shape that means anything at
  // that grain, so this stays shut rather than being scoped to near-nothing.
  "reports.view": false,
  "jobs.update": false,
  // An interviewer gives feedback on a person. Comp is not part of that job.
  "offers.view": false,
  "offers.manage": false,
  "inbox.view": false,
  // Referring a candidate is open to every role. The Interviewer role in
  // particular has always SAID it can refer — "Interviewers can be scheduled for
  // interviews, complete feedback, and refer candidates" — while holding none of
  // the keys that allow it, so the role did not do what its own description
  // promised.
  "jobs.viewPublished": true,
  "referrals.create": true,
  "referrals.viewOwn": true,
  "rewards.viewOwn": true,
};

const INTERVIEWER_NAVIGATION = {
  showDashboard: true,
  showLeads: true,
  showInbox: false,
  showJobs: false,
  showTours: true,
  showReports: false,
  showSettings: true,
  showUsers: false,
};

/**
 * The read-only tier: a department head who follows hiring in their area without taking part in it.
 *
 * Team Member's reach (`communities`, so the Users page can hold it to a department or a set of
 * communities) with none of its actions. It ships seeing broadly and an admin narrows it in Role
 * Management; what it can never do is widen into a write, which {@link VIEW_ONLY_PERMISSION_KEYS}
 * holds on both the matrix and the server.
 *
 * `offers.view` starts OFF because an offer carries compensation, the same default every role
 * below admin was given when the key arrived. `inbox.view` is not offered at all: the Inbox lets
 * its holder delete and hide candidate threads, so it is not a view.
 */
const VIEWER_PERMISSIONS = {
  "applicants.view": true,
  "applicants.create": false,
  "applicants.update": false,
  "applicants.delete": false,
  "jobs.view": true,
  "jobs.create": false,
  "jobs.update": false,
  "jobs.publish": false,
  "jobs.delete": false,
  "offers.view": false,
  "offers.manage": false,
  "inbox.view": false,
  "interviews.view": true,
  "interviews.schedule": false,
  "interviews.feedback": false,
  "reports.view": true,
  "settings.view": false,
  "settings.manage": false,
  "users.view": false,
  "users.manage": false,
  "apikeys.manage": false,
  "integrations.manage": false,
  "documents.view": false,
  "documents.manage": false,
  "jobs.viewPublished": true,
  // Not even a referral: inviting a candidate to apply is an action, and a Viewer only looks.
  // Their earlier referrals stay readable. migrations/0024 switched it off on existing rows.
  "referrals.create": false,
  "referrals.viewOwn": true,
  "rewards.viewOwn": true,
};

const VIEWER_NAVIGATION = {
  showDashboard: true,
  showLeads: true,
  showInbox: false,
  showJobs: true,
  showTours: true,
  showReports: true,
  showSettings: true,
  showUsers: false,
};

/**
 * The referral hub role: everyone in the company who is not on the hiring team.
 *
 * Deliberately NOT modelled on `interviewer`. That role reads like this persona
 * ("suitable for the rest of the organization") but carries `applicants.view`,
 * which would put the whole candidate pipeline — and, through the Inbox, salary
 * and offer context — in front of every employee. An employee refers people and
 * watches their own referrals; they never see the pipeline.
 *
 * `referrals.*` / `rewards.*` follow the precedent set by `clients.*` below: a
 * role may introduce its own permission namespace.
 */
const EMPLOYEE_PERMISSIONS = {
  "applicants.view": false,
  "applicants.create": false,
  "applicants.update": false,
  "applicants.delete": false,
  "jobs.view": false,
  "jobs.create": false,
  "jobs.publish": false,
  "jobs.delete": false,
  "interviews.view": false,
  "interviews.schedule": false,
  "interviews.feedback": false,
  "settings.view": false,
  "settings.manage": false,
  "users.view": false,
  "users.manage": false,
  "apikeys.manage": false,
  "integrations.manage": false,
  "documents.view": false,
  "documents.manage": false,
  "reports.view": false,
  "jobs.update": false,
  "offers.view": false,
  "offers.manage": false,
  "inbox.view": false,
  // Referral hub
  "jobs.viewPublished": true,
  "referrals.create": true,
  "referrals.viewOwn": true,
  "rewards.viewOwn": true,
};

/**
 * Nothing from the hiring dashboard. `showReferralHub` and `showRewards` are new
 * keys; a role that does not set them reads as false, because `shouldShow` in
 * sidebarConfig tests `=== true` rather than truthiness.
 */
const EMPLOYEE_NAVIGATION = {
  showDashboard: false,
  showLeads: false,
  showInbox: false,
  showJobs: false,
  showTours: false,
  showReports: false,
  showSettings: false,
  showUsers: false,
  showReferralHub: true,
  showRewards: true,
};

const CLIENT_PERMISSIONS = {
  "applicants.view": true,
  "applicants.create": false,
  "applicants.update": false,
  "applicants.delete": false,
  "jobs.view": true,
  "jobs.create": false,
  "jobs.publish": false,
  "jobs.delete": false,
  "interviews.view": false,
  "interviews.schedule": false,
  "interviews.feedback": false,
  "settings.view": false,
  "settings.manage": false,
  "users.view": false,
  "users.manage": false,
  "apikeys.manage": false,
  "integrations.manage": false,
  "documents.view": false,
  "documents.manage": false,
  // A client is external. Reports aggregate across the instance's whole desk,
  // including postings never shared with them.
  "reports.view": false,
  "jobs.update": false,
  // An external client must never reach comp or the internal mail threads.
  "offers.view": false,
  "offers.manage": false,
  "inbox.view": false,
  "clients.viewSharedJobs": true,
  "clients.viewSharedCandidates": true,
  "clients.viewResume": true,
  "clients.leaveFeedback": false,
  "jobs.viewPublished": true,
  "referrals.create": true,
  "referrals.viewOwn": true,
  "rewards.viewOwn": true,
};

const CLIENT_NAVIGATION = {
  showDashboard: false,
  showLeads: true,
  showInbox: false,
  showJobs: true,
  showTours: false,
  showReports: false,
  showSettings: false,
  showUsers: false,
};

const HIRE_ROLES: SystemRoleDefinition[] = [
  /**
   * Skypoint internal staff. Global scope only — one assignment reaches every
   * tenant, which is why it is the one role that must never be grantable from a
   * tenant's own admin UI or through skySupport's provisioning endpoint.
   *
   * `isHidden` keeps it out of role pickers AND out of UAM's `/roles` catalog
   * (`listRoles` filters `is_hidden`), so skySupport is never offered a role it
   * has no business granting. Assignment is by the one-shot
   * `scripts/migrations/seed-skypoint-admin.ts` script only.
   *
   * This tier existed in the original February RBAC design and was renamed away
   * into `super_admin` two days later, which is how one role came to span all
   * three scopes.
   */
  {
    name: "skypoint_admin",
    label: "Skypoint Admin",
    description:
      "Skypoint internal staff. Cross-tenant access to every instance. Not visible to customers and not grantable from the admin UI — seeded by script only.",
    category: "Admin",
    scope: "global",
    dataScope: "all",
    isSystem: true,
    isHidden: true,
    isImmutable: true,
    permissions: ALL_PERMISSIONS,
    navigation: ALL_NAVIGATION,
  },
  /**
   * RETIRED. Was the customer's top administrator across every instance of one
   * tenant. That reach is now expressed as Admin on each instance, which is what
   * skySupport can see and manage.
   *
   * Kept declared, hidden, so legacy rows still resolve and the boot seed keeps
   * the database row out of every picker. `scope` stays "tenant" because it
   * describes the rows that exist, not anything grantable.
   */
  {
    name: "super_admin",
    label: "Super Admin",
    description:
      "Retired. Existing holders are converted to Admin on every instance of their tenant.",
    category: "Admin",
    scope: "tenant",
    dataScope: "all",
    isSystem: true,
    isHidden: true,
    retired: true,
    isImmutable: true,
    permissions: ALL_PERMISSIONS,
    navigation: ALL_NAVIGATION,
  },
  {
    name: "admin",
    label: "Admin",
    description:
      "Full access to everything in a single instance: candidates, jobs, reports, settings, users, API keys, and integrations. Someone who runs several instances is an Admin on each. Suitable for Lead Recruiters, Head of Recruiting, and Recruiters.",
    category: "Admin",
    scope: "instance",
    dataScope: "all",
    isSystem: true,
    isHidden: false,
    // Locked.
    //
    // The admin tiers differ by SCOPE, not by power: this one has full access to
    // an instance, the platform tier across every tenant. A role defined as full
    // access cannot also be
    // editable — switch one permission off and "Admin" quietly means something
    // narrower in that instance, with nothing in the name to say so.
    //
    // A customer who wants a restricted administrator wants Team Member or a
    // custom role. They do not get there by weakening the role called Admin.
    isImmutable: true,
    permissions: ADMIN_PERMISSIONS,
    navigation: ALL_NAVIGATION,
  },
  {
    name: "team_member",
    label: "Team Member",
    description:
      "Team members can see all candidates and job posting reports. Suitable for junior to intermediate recruiters, recruiting coordinators, schedulers, and sourcers.",
    category: "Staff",
    scope: "instance",
    dataScope: "communities",
    isSystem: true,
    isHidden: false,
    isImmutable: false,
    permissions: TEAM_MEMBER_PERMISSIONS,
    navigation: TEAM_MEMBER_NAVIGATION,
  },
  {
    name: "limited_team_member",
    label: "Limited Team Member",
    description:
      "Limited team members see the candidates and reports for jobs where they are the recruiter or hiring manager, or on the job's team. Suitable for hiring managers and key decision makers in the hiring process.",
    formerDescriptions: [
      "Limited team members can see candidates they have access to and reports for job postings they are the owner/hiring manager for. Suitable for hiring managers and key decision makers in the hiring process.",
    ],
    category: "Staff",
    scope: "instance",
    dataScope: "assigned_jobs",
    isSystem: true,
    isHidden: false,
    isImmutable: false,
    permissions: LIMITED_TEAM_MEMBER_PERMISSIONS,
    navigation: LIMITED_TEAM_MEMBER_NAVIGATION,
  },
  {
    name: "viewer",
    label: "Viewer",
    description:
      "Viewers can see candidates, jobs, interviews and reports without acting on them: no stage moves, emails, notes or scheduling. Suitable for department heads and leaders who follow hiring in their area.",
    category: "Viewer",
    scope: "instance",
    dataScope: "communities",
    isSystem: true,
    isHidden: false,
    isImmutable: false,
    permissions: VIEWER_PERMISSIONS,
    navigation: VIEWER_NAVIGATION,
  },
  {
    name: "interviewer",
    label: "Interviewer",
    description:
      "Interviewers can be scheduled for interviews, complete feedback, and refer candidates. Suitable for the rest of the organization.",
    category: "Viewer",
    scope: "instance",
    dataScope: "assigned_applications",
    isSystem: true,
    isHidden: false,
    isImmutable: false,
    permissions: INTERVIEWER_PERMISSIONS,
    navigation: INTERVIEWER_NAVIGATION,
  },
  {
    name: "client",
    label: "Client",
    description:
      "External clients (hiring companies) with view-only access to jobs and candidate profiles explicitly shared with them by recruiters.",
    category: "Viewer",
    scope: "instance",
    dataScope: "client_shares",
    isSystem: true,
    isHidden: false,
    isImmutable: false,
    permissions: CLIENT_PERMISSIONS,
    navigation: CLIENT_NAVIGATION,
  },
  {
    name: "employee",
    label: "Employee",
    description:
      "Company employees who refer candidates and track their own referrals and rewards. No access to the hiring pipeline.",
    category: "Viewer",
    scope: "instance",
    dataScope: "none",
    isSystem: true,
    isHidden: false,
    isImmutable: false,
    permissions: EMPLOYEE_PERMISSIONS,
    navigation: EMPLOYEE_NAVIGATION,
  },
  // NOTE — `vendor` is deliberately absent.
  //
  // It exists in one developer's local database (inserted by hand in April with
  // empty permissions and `is_immutable = true`, so it granted nothing and could
  // not be edited or deleted) but in NEITHER dev NOR prod. Declaring it here
  // would make the boot seed create it in both, publishing a permission-less
  // role to skySupport as grantable in environments that never had one.
  //
  // The drift check in seedSystemRolesOnBoot reports it wherever it does exist,
  // which is the right outcome: a warning naming the row, not a silent
  // propagation of it.
];

/** The hire catalog alone, for readers that must not see another product's roles. */
export const HIRE_SYSTEM_ROLES: readonly SystemRoleDefinition[] = HIRE_ROLES;

/** Every role the boot seed writes, across products (spec §4.3). */
export const SYSTEM_ROLES: SystemRoleDefinition[] = [
  ...HIRE_ROLES,
  ...PERFORMANCE_ROLES,
  ...PERFORMANCE_IT_ROLES,
];

/**
 * The permission keys that describe what someone can DO inside skyHire — the
 * dimension the "grant only what you hold" ceiling compares on
 * (server/services/userManagementPolicy.ts).
 *
 * Client-portal keys (`clients.*`) are deliberately outside it. They describe
 * what an external client may see of what a recruiter shared with them, and no
 * internal role carries them because every internal role already sees more.
 * Counting them made a Client look more capable than an Admin, so no admin could
 * remove, re-role or block a client.
 */
export const CAPABILITY_PERMISSION_KEYS: readonly string[] = Object.keys(ALL_PERMISSIONS);

/** Every role name declared in code, across products: names are unique across products. */
export const SYSTEM_ROLE_NAMES: readonly string[] = SYSTEM_ROLES.map((role) => role.name);

// Every product: a lookup by name, and names are unique across products.
const BY_NAME = new Map(SYSTEM_ROLES.map((role) => [role.name, role]));

export function getSystemRole(name: string): SystemRoleDefinition | undefined {
  return BY_NAME.get(name);
}

/** The product a catalog role belongs to; hire unless the role says otherwise. */
export function productOf(role: Pick<SystemRoleDefinition, "product">): ProductKey {
  return role.product ?? DEFAULT_PRODUCT;
}

/**
 * The scope a role may be assigned at, or `null` for a role we do not declare —
 * a tenant's custom role, which is always instance-scoped by construction, or a
 * database-only role that has drifted in.
 */
export function scopeForRole(name: string): RoleScope | null {
  return BY_NAME.get(name)?.scope ?? null;
}

/**
 * What a retired role's rows are treated as until they are converted.
 *
 * One map, read through {@link currentRoleName}, so rank, labels and the UAM
 * roster all agree that a legacy `super_admin` is an Admin. Spelling that at
 * each call site is how the three would come to disagree.
 */
export const RETIRED_ROLE_REPLACEMENT: Readonly<Record<string, string>> = {
  super_admin: "admin",
};

/**
 * The role a row should be ranked, labelled and reported as: its own name, or
 * the replacement for a retired one. Authorisation that needs the stored name —
 * precedence, `grantsPlatformAdmin` on legacy global rows — keeps using it.
 */
export function currentRoleName(name: string): string {
  return RETIRED_ROLE_REPLACEMENT[name] ?? name;
}

/** Whether `name` is a retired catalog role. */
export function isRetiredRole(name: string | null | undefined): boolean {
  return !!name && BY_NAME.get(name)?.retired === true;
}

/**
 * The role that confers cross-tenant access. Anything comparing against a
 * global-scope assignment should ask for this by name rather than inferring
 * "global scope" means "platform admin" — a globally-scoped assignment of any
 * other role does not.
 */
export const PLATFORM_ADMIN_ROLE = "skypoint_admin";

/**
 * Roles that confer platform-admin reach when held at GLOBAL scope.
 *
 * `super_admin` is here for back-compatibility, not by design. Global-scope
 * `super_admin` rows predate `skypoint_admin` being restored — they are what the
 * February rename turned the platform tier into. They keep working until (and
 * after) they are migrated across, because a deploy that landed before the
 * migration ran would otherwise strip those people's access.
 *
 * What this list does fix: `isGlobalAdmin` used to be set from scope ALONE, so a
 * globally-scoped `interviewer`, `client` or `employee` — any role at all —
 * became a platform admin. Only these two do now.
 */
export const GLOBAL_ADMIN_ROLES: readonly string[] = [PLATFORM_ADMIN_ROLE, "super_admin"];

/** Whether an assignment of `roleName` at `scope` confers cross-tenant reach. */
export function grantsPlatformAdmin(roleName: string, scope: RoleScope): boolean {
  return scope === "global" && GLOBAL_ADMIN_ROLES.includes(roleName);
}

/** The scope a `(tenantId, instanceId)` pair describes. */
export function scopeOf(
  tenantId: string | null | undefined,
  instanceId: string | null | undefined,
): RoleScope {
  if (instanceId) return "instance";
  if (tenantId) return "tenant";
  return "global";
}

/**
 * Whether a role may be assigned at the scope a request is asking for.
 *
 * **New assignments only.** Existing rows are never re-checked: re-validating on
 * read would revoke access that was legal when granted, so resolution stays
 * exactly as it was and only the write path narrows.
 *
 * Roles we do not declare — a tenant's custom role — are instance-scoped like
 * every other grantable role. They used to be unconstrained, which is how a
 * custom role could be granted tenant-wide: reach skySupport cannot see.
 */
export function validateRoleScope(
  roleName: string,
  tenantId: string | null | undefined,
  instanceId: string | null | undefined,
): { valid: true } | { valid: false; reason: string } {
  if (isRetiredRole(roleName)) {
    return {
      valid: false,
      reason: "This role is no longer available. Grant Admin on each instance instead.",
    };
  }

  // The platform tier is granted by `scripts/migrations/seed-skypoint-admin.ts`
  // and nothing else — at no scope, through no route. This used to accept it at
  // global scope, which made the scope check the only thing standing between an
  // API caller and a platform row. The message deliberately does not name it.
  if (roleName === PLATFORM_ADMIN_ROLE) {
    return { valid: false, reason: "This role cannot be granted here." };
  }

  const allowed = scopeForRole(roleName) ?? "instance";

  const requested = scopeOf(tenantId, instanceId);
  if (requested === allowed) return { valid: true };

  const role = getSystemRole(roleName);
  const label = role?.label ?? roleName;

  return {
    valid: false,
    reason: `${label} is granted on a specific instance, not tenant-wide.`,
  };
}

/**
 * Roles that carry admin-or-above reach: the platform tier, the tenant's top
 * admin, and the instance admin.
 *
 * This exists because `#755` added `skypoint_admin` to the catalog but not to
 * the ~16 places that spelled the ladder out as string literals
 * (`name === "admin" || name === "super_admin"`), so the new tier silently read
 * as a non-admin everywhere and got a view-only UI. Compare through this helper
 * rather than re-spelling the list: adding a role should be one edit here, not a
 * fourteenth site to find later.
 *
 * **Name only, scope-blind.** It answers "is this role an admin role", not "does
 * this assignment reach here" — callers that hold a scope should keep their own
 * scope check alongside it (see `instanceService.getCurrentUser`), and callers
 * deciding cross-tenant reach want `grantsPlatformAdmin`.
 */
export const ADMIN_ROLES: readonly string[] = [PLATFORM_ADMIN_ROLE, "super_admin", "admin"];

/** Whether `roleName` is admin-or-above. See {@link ADMIN_ROLES}. */
export function isAdminRole(roleName: string | null | undefined): boolean {
  return !!roleName && ADMIN_ROLES.includes(roleName);
}

/**
 * Whether `roleName` is a platform tier — one that reaches across tenants when
 * held globally.
 *
 * Deliberately the same list as {@link GLOBAL_ADMIN_ROLES}, legacy global
 * `super_admin` included: the UI sites that call this (the header, the tenant
 * switcher) had `=== "super_admin"` before, so restricting it to
 * `skypoint_admin` alone would strip today's platform admins of their switcher
 * rather than admit the new tier. Where the scope is available, prefer
 * `grantsPlatformAdmin(roleName, scope)`, which is the precise test.
 */
export function isPlatformAdminRole(roleName: string | null | undefined): boolean {
  return !!roleName && GLOBAL_ADMIN_ROLES.includes(roleName);
}

/**
 * The label a role is SHOWN with.
 *
 * `skypoint_admin` is the backend's name for the platform scope, not a role a
 * person should ever see. Platform admins are kept off every list; the one place
 * a platform admin sees a role at all is their own Account page, where inside
 * the instance they are looking at they are exactly an Admin. A retired role
 * reads as its replacement, so "Super Admin" is never shown either.
 *
 * Every screen and API response that carries a role label for DISPLAY goes
 * through here, so the rule lives in one place.
 *
 * Display only. Authorisation keeps comparing the real name — gates and
 * `grantsPlatformAdmin` must still tell the tiers apart — which is why this
 * rewrites the label and never the name.
 */
export function displayRoleLabel(name: string, label: string): string {
  const shownAs = name === PLATFORM_ADMIN_ROLE ? "admin" : RETIRED_ROLE_REPLACEMENT[name];
  if (!shownAs) return label;
  return getSystemRole(shownAs)?.label ?? label;
}

/**
 * Every declared role except `client` — the roles that make someone an INTERNAL
 * user of an instance.
 *
 * Derived from the catalog rather than listed, because the hand-written list in
 * `clients.ts` was missing both `skypoint_admin` and `employee`: either could be
 * invited as a client and end up holding an internal role and a client role at
 * once, which is the mixed state that code's own comment says breaks the
 * role-based UI. A role added to the catalog is internal unless it says
 * otherwise.
 *
 * Hire only: "internal" is a hire notion (the client check reads hire assignments
 * alone), so a performance role never makes someone internal and inviting a
 * client never trips over a perf row.
 */
export const INTERNAL_ROLE_NAMES: readonly string[] = HIRE_SYSTEM_ROLES.map((r) => r.name).filter(
  (name) => name !== "client",
);

/** Whether holding `roleName` makes someone an internal user. See {@link INTERNAL_ROLE_NAMES}. */
export function isInternalRole(roleName: string | null | undefined): boolean {
  return !!roleName && INTERNAL_ROLE_NAMES.includes(roleName);
}

/**
 * Permission keys that are only OFFERED to the admin tier.
 *
 * `apikeys.manage` is the case that prompted this: the Role Management matrix
 * rendered a toggle for it against every role, but skyHire ships no API-keys
 * screen at all — no page, no route, no sidebar entry. Only the backend router
 * at `/api/v1/api-keys` exists. Showing a switch for a capability a role cannot
 * exercise, in a product area a customer cannot see, is how the matrix came to
 * read as "17 things you control" when most of it controls nothing.
 *
 * Keys listed here still exist on the admin tier's map — this hides the control
 * from roles that have no business holding it, it does not delete the key.
 */
/**
 * Sidebar switches a role is never offered, because its own home already answers for them.
 *
 * The Employee's home is the Referral Hub's own Dashboard. The hiring Dashboard refuses that role
 * outright, so offering the switch promised a page that answers "No dashboard for your role".
 */
export const NAVIGATION_NOT_OFFERED: Readonly<Record<string, readonly string[]>> = {
  employee: ["showDashboard"],
};

/** Whether Role Management should offer the `navKey` switch for `roleName`. */
export function isNavigationOfferedTo(
  navKey: string,
  roleName: string | null | undefined,
): boolean {
  if (!roleName) return true;
  return !(NAVIGATION_NOT_OFFERED[roleName] ?? []).includes(navKey);
}

export const ADMIN_ONLY_PERMISSION_KEYS: readonly string[] = ["apikeys.manage"];

/** Roles whose name promises they act on nothing. Role Management may narrow them, never widen them. */
export const VIEW_ONLY_ROLES: readonly string[] = ["viewer"];

/**
 * Everything a view-only role may be switched to. Each key either reads, or (the referral hub)
 * adds a person without acting on anyone already in the pipeline. `inbox.view` is absent on purpose:
 * its holder can delete and hide candidate threads.
 */
export const VIEW_ONLY_PERMISSION_KEYS: readonly string[] = [
  "applicants.view",
  "jobs.view",
  "interviews.view",
  "offers.view",
  "reports.view",
  "jobs.viewPublished",
  "referrals.viewOwn",
  "rewards.viewOwn",
];

/** Whether the Role Management matrix should offer `key` for `roleName`. */
export function isPermissionOfferedTo(key: string, roleName: string | null | undefined): boolean {
  if (roleName && VIEW_ONLY_ROLES.includes(roleName) && !VIEW_ONLY_PERMISSION_KEYS.includes(key)) {
    return false;
  }
  if (!ADMIN_ONLY_PERMISSION_KEYS.includes(key)) return true;
  return isAdminRole(roleName);
}

/**
 * The keys switched on in `permissions` that a view-only role may not hold. Empty for every other
 * role: the server refuses these, while the matrix only hides them.
 */
export function viewOnlyViolations(
  roleName: string,
  permissions: Readonly<Record<string, boolean>>,
): string[] {
  if (!VIEW_ONLY_ROLES.includes(roleName)) return [];
  return Object.entries(permissions)
    .filter(([key, on]) => on === true && !VIEW_ONLY_PERMISSION_KEYS.includes(key))
    .map(([key]) => key);
}

/**
 * Leaving input on a candidate (a note, suggested interview questions) takes ANY one of these.
 *
 * It used to take only `applicants.view`, so that the Interviewer, who holds no candidate write,
 * could note what they assessed. That also let a read-only Viewer write. Every system role that
 * writes today holds one of these keys (Team Member edits candidates; Limited Team Member and
 * Interviewer give feedback), so the Viewer is the only system role it stops.
 */
export const CANDIDATE_INPUT_PERMISSIONS: readonly string[] = [
  "applicants.update",
  "interviews.feedback",
];

/** Whether `permissions` may leave input on a candidate. See {@link CANDIDATE_INPUT_PERMISSIONS}. */
export function canLeaveCandidateInput(
  permissions: Readonly<Record<string, boolean>> | null | undefined,
): boolean {
  return CANDIDATE_INPUT_PERMISSIONS.some((key) => permissions?.[key] === true);
}

// ─── Toggle dependencies ────────────────────────────────────────────────────

/**
 * Which permission a permission needs before it can mean anything.
 *
 * The Role Management matrix rendered seventeen independent switches over a set
 * of capabilities that are not independent at all. "Delete" without "View" is a
 * role that may delete a candidate it cannot open; "Schedule" without
 * "View interviews" is a role whose scheduling call 403s on the read that
 * follows it. Every such pairing is a state an admin could save and then watch
 * do nothing, which is what makes the screen read as broken.
 *
 * The edges below are drawn from the ROUTE GATES, not from intuition — each one
 * names a case where the server would refuse the child while the parent is off:
 *
 *  - `applicants.*` writes run behind `applicants.view` (applicants.ts mounts
 *    `requirePermissionWhenAssigned("applicants.view")` above the method gate,
 *    so every write is also a read).
 *  - `jobs.*` writes are gated per route, but each write's own screen is reached
 *    through `GET /jobs`, which is `requirePermission("jobs.view")`.
 *  - `interviews.*` sit under `interviewsRouter.use(... "interviews.view")`.
 *  - offers: `hiring.ts` gates GET on `offers.view` and every write on
 *    `offers.manage`, and managing an offer reads it first. Sending one needs
 *    `applicants.update` as well: the offer reaches the candidate as an email
 *    through `POST /email/send`, which answers to that key, so without it
 *    "Create & send offers" could create an offer and never send it.
 *  - `settings.manage` / `users.manage` already ship their `.view` half through
 *    the paired switch; the edge is here so a role row that predates the pairing
 *    is repaired rather than silently left half-set.
 *  - the referral hub: `rewards.viewOwn` shows what `referrals.viewOwn` earned,
 *    and both hang off the open-roles list behind `jobs.viewPublished`.
 *
 * Deliberately NOT here: `inbox.view`, `apikeys.manage`, `integrations.manage`.
 * Each is gated on itself alone and reads perfectly well as a standalone switch.
 */
export const PERMISSION_PREREQUISITES: Readonly<Record<string, readonly string[]>> = {
  "applicants.create": ["applicants.view"],
  "applicants.update": ["applicants.view"],
  "applicants.delete": ["applicants.view"],
  "jobs.create": ["jobs.view"],
  "jobs.update": ["jobs.view"],
  "jobs.publish": ["jobs.view"],
  "jobs.delete": ["jobs.view"],
  "interviews.schedule": ["interviews.view"],
  "interviews.feedback": ["interviews.view"],
  "offers.manage": ["offers.view", "applicants.update"],
  "settings.manage": ["settings.view"],
  "users.manage": ["users.view"],
  "referrals.create": ["jobs.viewPublished"],
  "referrals.viewOwn": ["jobs.viewPublished"],
  "rewards.viewOwn": ["referrals.viewOwn"],
  // A candidate's documents are reached through the candidate.
  "documents.view": ["applicants.view"],
  "documents.manage": ["documents.view"],
};

/**
 * The permission a sidebar entry needs before showing it is honest.
 *
 * A nav flag only decides whether a link is drawn. Turning "Reports" on for a
 * role without `reports.view` draws a link to a page whose router answers 403 —
 * the same shape of failure as #806's Client dashboard, which appears in the
 * sidebar and then refuses to open.
 *
 * `showSettings` has no entry ON PURPOSE. The Settings section carries
 * "Help + Support" and "Candidate Communication Info" for every role, and Team
 * Member holds `showSettings` with `settings.manage` off — requiring the
 * permission would take Settings away from the role that uses it today.
 */
export const NAVIGATION_REQUIRES: Readonly<Record<string, string>> = {
  showDashboard: "applicants.view",
  showLeads: "applicants.view",
  showInbox: "inbox.view",
  showJobs: "jobs.view",
  showTours: "interviews.view",
  showReports: "reports.view",
  showUsers: "users.view",
  showReferralHub: "referrals.viewOwn",
  showRewards: "rewards.viewOwn",
};

/** Every permission key that lists `key` among its prerequisites, transitively. */
export function permissionDependentsOf(key: string): string[] {
  const found = new Set<string>();
  const queue = [key];
  while (queue.length > 0) {
    const current = queue.shift() as string;
    for (const [child, parents] of Object.entries(PERMISSION_PREREQUISITES)) {
      if (found.has(child) || !parents.includes(current)) continue;
      found.add(child);
      queue.push(child);
    }
  }
  return [...found];
}

/** Every permission `key` needs, transitively. */
export function permissionPrerequisitesOf(key: string): string[] {
  const found = new Set<string>();
  const queue = [...(PERMISSION_PREREQUISITES[key] ?? [])];
  while (queue.length > 0) {
    const current = queue.shift() as string;
    if (found.has(current)) continue;
    found.add(current);
    queue.push(...(PERMISSION_PREREQUISITES[current] ?? []));
  }
  return [...found];
}

/** Whether every prerequisite of `key` is currently held. */
export function arePrerequisitesMet(key: string, permissions: Record<string, boolean>): boolean {
  return permissionPrerequisitesOf(key).every((parent) => permissions[parent] === true);
}

/**
 * What still has to be turned on before a SWITCH covering `keys` can be.
 *
 * One switch may write several keys — "Manage users" writes `users.manage` and
 * `users.view` together — and `users.manage` requires `users.view`. Measured
 * against the stored map alone, such a switch is blocked by a prerequisite it
 * would satisfy itself the moment it was flipped, which disabled the two
 * controls a customer most wants to reach. So a prerequisite inside the covered
 * set does not count against it.
 *
 * Returns the keys, de-duplicated, in prerequisite order; empty means the switch
 * is free to turn on.
 */
export function unmetPrerequisitesFor(
  keys: readonly string[],
  permissions: Record<string, boolean>,
): string[] {
  const covered = new Set(keys);
  return [
    ...new Set(
      keys
        .flatMap((key) => permissionPrerequisitesOf(key))
        .filter((parent) => !covered.has(parent) && permissions[parent] !== true),
    ),
  ];
}

/**
 * Apply a permission change together with everything it implies, as ONE map.
 *
 * Turning a switch on turns on what it depends on; turning one off turns off
 * what depends on it, and drops any sidebar flag whose permission just went
 * away. The whole cascade is returned as a single object so it is written in a
 * single request — a half-applied cascade is the state this exists to prevent.
 */
export function applyPermissionChange(
  permissions: Record<string, boolean>,
  navigation: Record<string, boolean>,
  keys: readonly string[],
  nextValue: boolean,
): { permissions: Record<string, boolean>; navigation: Record<string, boolean> } {
  const nextPermissions = { ...permissions };
  const nextNavigation = { ...navigation };

  for (const key of keys) {
    nextPermissions[key] = nextValue;
    if (nextValue) {
      for (const parent of permissionPrerequisitesOf(key)) nextPermissions[parent] = true;
    } else {
      for (const child of permissionDependentsOf(key)) nextPermissions[child] = false;
    }
  }

  // A sidebar entry cannot outlive the permission behind it.
  for (const [navKey, required] of Object.entries(NAVIGATION_REQUIRES)) {
    if (nextPermissions[required] !== true) nextNavigation[navKey] = false;
  }

  return { permissions: nextPermissions, navigation: nextNavigation };
}

/**
 * Whether a sidebar switch may be flipped on — i.e. whether the page behind it
 * would actually open. `showSettings` and anything unlisted are always free.
 */
export function isNavigationAvailable(
  navKey: string,
  permissions: Record<string, boolean>,
): boolean {
  const required = NAVIGATION_REQUIRES[navKey];
  if (!required) return true;
  return permissions[required] === true;
}
